/**
 * GlmLightningLayer.tsx — البرق **المرصود** (GOES GLM + MTG-LI): وميضٌ وصوت، بزمن الشريط.
 *
 * ── علاقتها بـLightningLayer الموجودة ─────────────────────────────────────────
 *
 * تلك طبقةٌ **توقّعية** مشتقّة من النموذج، وهذه طبقةُ **رصد**: «أين ضرب البرق فعلاً؟».
 * ولذلك لا تُرسم فوق ساعة توقّع قادمة — رصدٌ وتوقّع في صورة واحدة أسوأ من ألّا يُعرض.
 *
 * ── الزمن: ثلاث حالات ─────────────────────────────────────────────────────────
 *
 * لكل ومضة **زمن وقوع مطلق** (‏generatedAt − t). والطبقة تعرض ما وقع قبل «لحظة العرض»
 * بمدّة الأثر (TRAIL)، وتُطلق **وميضاً** (وصوتاً) لكل ومضة تعبرها لحظة العرض:
 *
 *   ① حلقة الرادار/القمر: لحظة العرض = زمن الشريط (‏`epoch`). فتظهر كل ومضة مع صورة
 *      الرادار في وقتها، وتومض حين تصلها الحلقة — لا طبقةٌ ثابتة فوق حلقة متحرّكة.
 *   ② «الآن» الساكن: لحظة العرض = الآن ناقص LIVE_LAG_S. الملفّ يصل كل ~90 ثانية بما وقع
 *      قبله، فنُعيد تشغيله متأخّراً دقيقتين ونصفاً: تومض الومضات **واحدةً واحدة** في
 *      توقيتها الحقيقي بدل أن تظهر كتلةً كل تسعين ثانية. هذا ما يجعلها تبدو حيّة.
 *   ③ القفز للخلف (عودة الحلقة لبدايتها، سحب الشريط للوراء): لا وميض — الوميض يعني
 *      «وقع الآن»، والرجوع ليس وقوعاً.
 *
 * ── لماذا كانفاس واحد، وتكتيل في شبكة شاشة ──────────────────────────────────
 *
 * النافذة عشرات الآلاف من الومضات. عقدة DOM لكلٍّ منها تُثقل كل حركة، والكانفاس يرسمها
 * في تمريرة. ورسم هالة لكل ومضة يُنتج **كتلاً صفراء عديمة الشكل** عند التصغير (تراكم
 * آلاف الهالات في بضع مئات من البكسلات)، فنُكتّل في شبكة بالبكسل: شرارة لكل خليّة،
 * والكثافة تُقرأ من سطوعها وحجمها. وعند التكبير تصير كل خليّة ومضةً — رصدٌ فرديّ.
 */

import { useEffect, useRef, useState } from 'react';
import { useMapRef } from './MapContext';
import { cameraKey } from './mapCameraKey';
import {
    fetchLightning, fetchLightningHours, LIGHTNING_POLL_MS,
    type LightningHour, type LightningWindow,
} from '../../services/glmLightningService';
import { BOLT_PATH_D, BOLT_UNITS } from './lightningGlyph';
import { useWeatherStore } from '../../store/weatherStore';
import { playThunder, unlockThunder } from './thunderSound';

/** عدد الومضات الذي يبلغ عنده الرمز أقصى حجمه (تدرّج لوغاريتمي). */
const SATURATION_COUNT = 24;

/**
 * رمز صاعقة **في كل مستويات التكبير** (طلب 2026-09-14) — لا نقاط وهج عند التصغير.
 * الحجم يتبع التكبير دائماً؛ والازدحام يزيده قليلاً فتُقرأ الكثافة من الحجم. والتكتيل
 * باقٍ: الخليّة = الرمز + هامش، فرمزان لا يتراكبان ولا تذوب العاصفة كتلةً واحدة.
 *
 * ── هيئتان لا واحدة (طلب 2026-09-16) ────────────────────────────────────────
 * اللون يجب أن يُقرأ على ما تحته:
 *   • **فوق الرادار**: أبيض صغير. الرادار حقلٌ ملوّن (أزرق→أحمر)، والأصفر فيه يُخلط
 *     بألوان الهطول نفسها فلا يُميَّز؛ والأبيض غريبٌ عنها كلّها.
 *   • **فوق صور الأقمار**: أصفر أكبر. صور السحب بيضاء ورمادية، فرمزٌ أبيض عليها
 *     يختفي — وهو ما رصده العميل بالضبط. الأصفر هو النقيض المقروء هناك.
 * ولذلك الحجم أيضاً أكبر قليلاً فوق الأقمار: خلفيّتها أكثر ضجيجاً بصرياً.
 */
interface Look {
    /** حجم الرمز بالبكسل عند تكبيرٍ ما. */
    px: (zoom: number) => number;
    /** أقصى زيادة من الازدحام. */
    density: number;
    /** تعبئة الرمز. */
    fill: string;
    /** مركز الهالة وحافّتها (‏"r,g,b") وشدّتها. */
    halo: [string, string, number];
    /** ألوان قلب الوميض: المركز، المنتصف، الحافّة الشفّافة. */
    burst: [string, string, string];
}

const LOOKS: Record<'radar' | 'satellite', Look> = {
    radar: {
        px: (z) => Math.max(7, Math.min(11, 7 + (z - 2) * 0.8)),
        density: 2,
        fill: '#ffffff',
        halo: ['225,235,255', '200,215,255', 0.3],
        burst: ['255,255,255', '230,238,255', '220,230,255'],
    },
    satellite: {
        px: (z) => Math.max(9, Math.min(15, 9 + (z - 2) * 1.2)),
        density: 3,
        fill: '#ffd447',
        halo: ['255,214,90', '255,170,30', 0.42],
        burst: ['255,255,255', '255,240,170', '255,220,120'],
    },
};
const GLYPH_GAP_PX = 4;
const BOLT_PATH = typeof Path2D !== 'undefined' ? new Path2D(BOLT_PATH_D) : null;

/**
 * مدّة بقاء أثر الومضة بعد وقوعها (ثوانٍ).
 *
 * حيّاً عشرون دقيقة (سلوك الطبقة السابق). وفي الحلقة كانت عشراً فبدت العاصفة **خاليةً
 * من البرق**: الحلقة تعرض ساعتين في نحو إحدى عشرة ثانية، فأثرُ عشر دقائق يعني أن كل
 * ومضة تومض جزءاً من ثانية ثم تختفي — والعين لا تلتقط ذلك (ملاحظة العميل 2026-09-16:
 * «البرق ما يظهر كامل… المفروض يظهر من الإطارات السابقة»).
 *
 * فصارت عشرين دقيقة في الحلقة أيضاً: أربع خطوات رادار (‏5 دقائق لكل خطوة)، فتُقرأ
 * العاصفة الرعدية ككتلةٍ متحرّكة لها ذيل بدل ومضاتٍ متناثرة. وأطول من ذلك يُلطّخ:
 * تبقى ومضات نصف ساعة على الشاشة فلا يُعرف أين البرق **الآن**.
 */
const TRAIL_LIVE_S = 1200;
const TRAIL_LOOP_S = 1200;

/** تأخير إعادة التشغيل الحيّ (ثوانٍ): ≥ عمر الملفّ (~90ث) + تأخّر النشر (~45ث). */
const LIVE_LAG_S = 150;

/** مدّة الوميض (مللي) وسقف عدد الومضات الوامضة في آن — حماية الأداء في عاصفة كثيفة. */
const BURST_MS = 700;
const MAX_BURSTS = 80;

/** إيقاع فحص تقدّم الزمن على خريطة ساكنة (مللي). */
const TICK_MS = 250;

interface Props {
    enabled: boolean;
    /**
     * الطبقة المعروضة تحت البرق. تحكم اللون والحجم، و**أيّ الومضات تُعرض**:
     * فوق الرادار ما داخل التغطية فقط، وفوق الأقمار كلّ ما رُصد.
     */
    mode: 'radar' | 'satellite';
    /** زمن الشريط (ثوانٍ) على محور الرصد، أو null للعرض الحيّ. */
    epoch: number | null;
}

interface Burst { lon: number; lat: number; start: number }

export function GlmLightningLayer({ enabled, epoch, mode }: Props) {
    const mapRef = useMapRef();
    const soundOn = useWeatherStore((s) => s.lightningSoundEnabled);
    const [win, setWin] = useState<LightningWindow | null>(null);

    useEffect(() => {
        if (!enabled) { setWin(null); return; }
        let cancelled = false;
        const load = () => { void fetchLightning().then((w) => { if (!cancelled) setWin(w); }); };
        load();
        const id = window.setInterval(load, LIGHTNING_POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    /*
     * ── ما قبل النافذة الحيّة: ملفّات الساعات ────────────────────────────────
     * النافذة الحيّة ساعتان وشريط القمر ~5.5: حين يقع المؤشّر (مع أثره) قبل بدايتها
     * تُطلب الساعات الناقصة. المفتاح ساعة المؤشّر لا ثانيته، فالطلب مرّة لكل ساعة.
     */
    const liveStart = win ? Date.parse(win.generatedAt) / 1000 - win.windowMinutes * 60 : null;
    const needFrom = epoch !== null && liveStart !== null && epoch - TRAIL_LOOP_S < liveStart
        ? Math.floor((epoch - TRAIL_LOOP_S) / 3600) * 3600 : null;
    const [hist, setHist] = useState<LightningHour[]>([]);
    useEffect(() => {
        if (!enabled || needFrom === null || liveStart === null) return;
        let cancelled = false;
        // ساعةٌ إضافية للأمام: التشغيل يتقدّم، فتصل التالية قبل أن يبلغها المؤشّر.
        void fetchLightningHours(needFrom, Math.min(liveStart, needFrom + 2 * 3600)).then((h) => {
            if (cancelled || h.length === 0) return;
            setHist((prev) => {
                const m = new Map(prev.map((x) => [`${x.hour}|${x.t.length}|${x.lat[0]}`, x]));
                for (const x of h) m.set(`${x.hour}|${x.t.length}|${x.lat[0]}`, x);
                return m.size === prev.length ? prev : [...m.values()];
            });
        });
        return () => { cancelled = true; };
    }, [enabled, needFrom, liveStart]);
    useEffect(() => { if (!enabled) setHist([]); }, [enabled]);

    // أزمنة الوقوع المطلقة تُحسب مرّة لكل ملفّ لا لكل إطار.
    const dataRef = useRef<{ lat: number[]; lon: number[]; ts: Float64Array; r: number[] | null } | null>(null);
    useEffect(() => {
        if (!win) { dataRef.current = null; return; }
        const gen = Date.parse(win.generatedAt) / 1000;
        const start = gen - win.windowMinutes * 60;
        // من الساعات ما قبل النافذة الحيّة فقط — داخلها الحيّ أحدث وأدقّ.
        let extra = 0;
        for (const h of hist) for (const t of h.t) if (h.hour + t < start) extra++;
        const n = win.t.length + extra;
        const ts = new Float64Array(n);
        const lat = win.lat.slice(), lon = win.lon.slice();
        const r = win.r ? win.r.slice() : new Array<number>(win.t.length).fill(1);
        for (let i = 0; i < win.t.length; i++) ts[i] = gen - win.t[i];
        let j = win.t.length;
        for (const h of hist) {
            for (let i = 0; i < h.t.length; i++) {
                const t = h.hour + h.t[i];
                if (t >= start) continue;
                ts[j++] = t; lat.push(h.lat[i]); lon.push(h.lon[i]); r.push(h.r?.[i] ?? 1);
            }
        }
        dataRef.current = { lat, lon, ts, r };
    }, [win, hist]);

    // الحلقة مربوطة بالخريطة مرّة واحدة؛ ما يتغيّر (الزمن، الصوت) يُقرأ من refs.
    const epochRef = useRef<number | null>(epoch);
    epochRef.current = epoch;
    const soundRef = useRef(soundOn);
    soundRef.current = soundOn;
    // الهيئة تتغيّر بتبديل الطبقة، والحلقة مربوطة مرّة واحدة — فتُقرأ من ref كالزمن.
    const lookRef = useRef(LOOKS[mode]);
    lookRef.current = LOOKS[mode];

    /*
     * سبب «المفتاح مشغّل ولا صوت»: الاختيار يُحفظ، لكن بعد إعادة تحميل الصفحة يُنشئ
     * المتصفّح سياق الصوت **معلّقاً** ولا يفتحه إلا من لمسة مستخدم — ولم تكن هناك لمسة
     * على المفتاح هذه المرّة. فأوّل لمسة أو ضغطة زرّ في أي مكان على الصفحة تفتحه.
     */
    useEffect(() => {
        if (!soundOn) return;
        const unlock = () => unlockThunder();
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, [soundOn]);

    useEffect(() => {
        if (!enabled) return;
        let stop = () => { /* يُستبدل بعد التركيب */ };
        let pollId = 0;
        // الخريطة قد لا تكون جاهزة عند أوّل تركيب، و`mapRef` ثابت فلا يُعيد تشغيل
        // التأثير حين تجهز — فننتظر بالاستطلاع (وقع الغياب الصامت فعلاً من قبل).
        const attach = (): boolean => {
            const wrap = mapRef.current;
            if (!wrap) return false;
            stop = mount(wrap.getMap());
            return true;
        };
        if (!attach()) pollId = window.setInterval(() => { if (attach()) window.clearInterval(pollId); }, 200);
        return () => { window.clearInterval(pollId); stop(); };

        function mount(map: ReturnType<NonNullable<typeof mapRef.current>['getMap']>) {
            const container = map.getContainer();
            const canvas = document.createElement('canvas');
            canvas.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:11;';
            container.appendChild(canvas);
            const ctx = canvas.getContext('2d');
            if (!ctx) { canvas.remove(); return () => { /* لا شيء */ }; }

            let lastCam: string | null = null;
            let lastT: number | null = null;       // لحظة العرض في آخر رسم (ثوانٍ)
            /*
             * ── إسقاطٌ مخزَّن لكل ومضة، يُعاد عند تحرّك الكاميرا وحده ───────────────
             *
             * أثناء تشغيل الرادار يتقدّم الزمن والكاميرا ساكنة، فكانت كل نبضة تُعيد
             * `map.project` لكل ومضة داخل النافذة — نحو 25 ألفاً — لتعطي الموضع نفسه.
             * قيس على الموقع الحيّ في 2026-09-21: 25 ألف إسقاط = 24.3 مللي للنبضة
             * الواحدة، والقراءة من مخزنٍ جاهز 1.5 مللي. وكانت دالّة الرسم هذه أثقل ما
             * بقي على الخيط الرئيسي في تتبّع اليوم نفسه (3963 مللي).
             *
             * (جُرّب قبله ترتيبُ الومضات بالزمن وبحثٌ ثنائي عن النافذة، فخرج **أبطأ**:
             * حلقة التصفية تمرّ على 77 ألفاً في أقلّ من مللي، والترتيب يكسر تتابع
             * الذاكرة. الكلفة لم تكن في التصفية بل في الإسقاط.)
             */
            let lastDrawAt = 0;                    // آخر رسمة فعلية (performance.now)
            let projFor: unknown = null;
            let projCam: string | null = null;
            let px = new Float32Array(0), py = new Float32Array(0), pOk = new Uint8Array(0);
            /*
             * المقاس من `ResizeObserver` لا من `clientWidth` في كل رسم: الرسم هنا يقع مع
             * كل نبضة أثناء تشغيل الرادار (زمن العرض يتقدّم)، و`clientWidth` تُجبر
             * المتصفّح على حساب التخطيط قبل أن تُجيب. تتبّع 2026-09-21 وضعها في رأس
             * الخيط الرئيسي: 3274 مللي من هذا السطر وحده — وهي العلّة نفسها التي
             * أُصلحت في أسماء المدن قبل يوم.
             */
            let cw = container.clientWidth, ch = container.clientHeight;
            const ro = new ResizeObserver(() => { cw = container.clientWidth; ch = container.clientHeight; });
            ro.observe(container);
            let bursts: Burst[] = [];
            let raf = 0;

            // حيّاً تُقرَّب لثانية كاملة: بلا تقريب يتغيّر الزمن كل مللي فيُعاد المرور على
            // كل الومضات (حتى 150 ألفاً) مع كل حدث `render` — وهو يتكرّر أثناء حلقة الرادار.
            const displayTime = (): number =>
                epochRef.current ?? Math.floor(Date.now() / 1000 - LIVE_LAG_S);

            const draw = () => {
                raf = 0;
                const data = dataRef.current;
                const T = displayTime();
                const nowMs = performance.now();
                const look = lookRef.current;
                const key = `${cameraKey(map)}|${look.fill}`;
                const moved = key !== lastCam;
                const advanced = lastT === null || T !== lastT;
                bursts = bursts.filter((b) => nowMs - b.start < BURST_MS);
                if (!moved && !advanced && bursts.length === 0) return;
                /*
                 * ── رسمة واحدة في الإطار ─────────────────────────────────────────────
                 *
                 * ثلاثة مشغّلات للرسم نفسه: `render`، وحلقة rAF خاصّة ما دامت ومضاتٌ حيّة،
                 * ونبضة زمنية. وأثناء تشغيل الرادار يعمل الأوّلان معاً — الرادار يطلب
                 * إعادة رسمٍ كل إطار، والومضات الجديدة تُبقي الحلقة دائرة — فكان البرق
                 * يُرسم مرّتين في الإطار الواحد. تتبّع 2026-09-21: هذه الدالّة أكبر مستدعٍ
                 * لـrequestAnimationFrame (589 مللي).
                 *
                 * والكاميرا المتحرّكة استثناء: رسمةٌ ثانية حينها تلحق بموضعٍ جديد فعلاً،
                 * وتأخيرها إطاراً كان سيُظهر الرموز تسبح خلف الخريطة أثناء السحب.
                 */
                if (!moved && nowMs - lastDrawAt < 12) {
                    if (bursts.length > 0 && !raf) raf = requestAnimationFrame(draw);
                    return;
                }
                lastDrawAt = nowMs;
                lastCam = key;

                const w = cw, h = ch;
                const dpr = Math.min(window.devicePixelRatio || 1, 2);
                if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
                    canvas.width = w * dpr; canvas.height = h * dpr;
                    canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
                }
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                ctx.clearRect(0, 0, w, h);
                if (!data) { lastT = T; return; }

                const trail = epochRef.current === null ? TRAIL_LIVE_S : TRAIL_LOOP_S;
                const b = map.getBounds();
                const north = b.getNorth() + 1, south = b.getSouth() - 1;
                const west = b.getWest() - 1, east = b.getEast() + 1;

                // ومضات عبرتها لحظة العرض منذ آخر رسم ⇒ وميض. للأمام فقط وبقفزة معقولة:
                // الرجوع للخلف أو قفزة أطول من الأثر (فتح الطبقة، سحب بعيد) ليست «وقوعاً».
                const prev = lastT;
                const fresh = prev !== null && T > prev && T - prev <= trail;
                let newCount = 0;

                const basePx = look.px(map.getZoom());
                const cell_px = basePx + look.density + GLYPH_GAP_PX;
                const cells = new Map<number, { n: number; age: number; sx: number; sy: number }>();
                const cols = Math.ceil(w / cell_px) + 2;

                // فوق الرادار: ما لا رادار تحته لا يُعرض — وإلّا بدا الرادار ناقصاً حيث
                // يومض البرق بلا مطر. وفوق الأقمار لا استثناء: كلاهما رصدٌ من المدار.
                const covered = look === LOOKS.radar ? data.r : null;
                const cam = cameraKey(map);
                if (projFor !== data || projCam !== cam) {
                    const n = data.ts.length;
                    if (px.length !== n) { px = new Float32Array(n); py = new Float32Array(n); pOk = new Uint8Array(n); }
                    else pOk.fill(0);
                    projFor = data; projCam = cam;
                }
                for (let i = 0; i < data.ts.length; i++) {
                    if (covered && !covered[i]) continue;
                    const la = data.lat[i], lo = data.lon[i];
                    if (la > north || la < south || lo < west || lo > east) continue;
                    const age = T - data.ts[i];
                    if (age < 0 || age > trail) continue;      // لم تقع بعد، أو انقضى أثرها
                    let x: number, y: number;
                    if (pOk[i]) { x = px[i]; y = py[i]; }
                    else { const p = map.project([lo, la]); x = p.x; y = p.y; px[i] = x; py[i] = y; pOk[i] = 1; }
                    if (x < -cell_px || x > w + cell_px || y < -cell_px || y > h + cell_px) continue;

                    if (fresh && data.ts[i] > (prev as number)) {
                        newCount += 1;
                        if (bursts.length < MAX_BURSTS) bursts.push({ lon: lo, lat: la, start: nowMs });
                    }

                    const cx = Math.floor(x / cell_px), cy = Math.floor(y / cell_px);
                    const k = (cy + 1) * cols + (cx + 1);
                    const cell = cells.get(k);
                    if (cell) {
                        cell.n += 1; cell.sx += x; cell.sy += y;
                        if (age < cell.age) cell.age = age;
                    } else {
                        cells.set(k, { n: 1, age, sx: x, sy: y });
                    }
                }
                lastT = T;

                // شرارة واحدة لكل دفعة ظاهرة، شدّتها من عددها — لا صوتٌ لكل ومضة.
                if (newCount > 0 && soundRef.current) {
                    playThunder(Math.log(1 + newCount) / Math.log(1 + 30));
                }

                // ── الأثر: هالة خافتة تحت كل رمز، والرمز نفسه يخفت مع العمر ──────
                // الهالة وحدها بـ`lighter` (ضوءٌ يتراكم)؛ الرمز لاحقاً بـsource-over كي
                // لا يُشبع تراكبُه اللونَ أبيض فتضيع هيئة الصاعقة.
                ctx.globalCompositeOperation = 'lighter';
                const glyphs: { x: number; y: number; a: number; size: number }[] = [];
                for (const cell of cells.values()) {
                    const x = cell.sx / cell.n, y = cell.sy / cell.n;
                    const freshness = 1 - Math.sqrt(Math.min(1, cell.age / trail));
                    const density = Math.min(1, Math.log(1 + cell.n) / Math.log(1 + SATURATION_COUNT));
                    const size = basePx + look.density * density;
                    const a = 0.3 + 0.7 * freshness;
                    const halo = size * 0.8;
                    const glow = ctx.createRadialGradient(x, y, 0, x, y, halo);
                    glow.addColorStop(0, `rgba(${look.halo[0]},${(look.halo[2] * a).toFixed(3)})`);
                    glow.addColorStop(1, `rgba(${look.halo[1]},0)`);
                    ctx.fillStyle = glow;
                    ctx.beginPath(); ctx.arc(x, y, halo, 0, Math.PI * 2); ctx.fill();
                    glyphs.push({ x, y, a, size });
                }

                // ── الوميض: ومضة بيضاء وحلقة تتّسع ثم تتلاشى ──────────────────
                for (const bu of bursts) {
                    const k = (nowMs - bu.start) / BURST_MS;       // 0 → 1
                    const p = map.project([bu.lon, bu.lat]);
                    const fade = 1 - k;
                    // الوميض والحلقة بمقياس الرمز نفسه — كانا ثابتين (22+ بكسل) فيطغيان
                    // على رمزٍ صغير عند التصغير.
                    const core = basePx * 0.9 * (1 - 0.4 * k);
                    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, core);
                    g.addColorStop(0, `rgba(${look.burst[0]},${(0.9 * fade).toFixed(3)})`);
                    g.addColorStop(0.5, `rgba(${look.burst[1]},${(0.4 * fade).toFixed(3)})`);
                    g.addColorStop(1, `rgba(${look.burst[2]},0)`);
                    ctx.fillStyle = g;
                    ctx.beginPath(); ctx.arc(p.x, p.y, core, 0, Math.PI * 2); ctx.fill();
                    ctx.strokeStyle = `rgba(255,255,255,${(0.6 * fade).toFixed(3)})`;
                    ctx.lineWidth = 1;
                    ctx.beginPath(); ctx.arc(p.x, p.y, basePx * (0.6 + 0.9 * k), 0, Math.PI * 2); ctx.stroke();
                }
                ctx.globalCompositeOperation = 'source-over';

                // رمز الصاعقة: تعبئة بيضاء بحدٍّ داكن رفيع — بلا الحدّ يذوب الأبيض في الغيوم
                // وصور القمر الصناعي تحته. الحدّ يُرسم قبل التعبئة فيبقى نصفه الخارجي وحده.
                // القسمة على `k`: السياق مُحجَّم، فعرض الخطّ يُقاس بوحدات المسار لا بالبكسل.
                const drawBolt = (x: number, y: number, size: number, alpha: number, fill: string) => {
                    if (!BOLT_PATH) return;
                    const k = size / BOLT_UNITS;
                    ctx.save();
                    ctx.translate(x - size / 2, y - size / 2);
                    ctx.scale(k, k);
                    ctx.globalAlpha = alpha;
                    ctx.lineJoin = 'round';
                    ctx.lineWidth = 1.8 / k;
                    ctx.strokeStyle = 'rgba(10,14,28,0.85)';
                    ctx.stroke(BOLT_PATH);
                    ctx.fillStyle = fill;
                    ctx.fill(BOLT_PATH);
                    ctx.restore();
                };
                for (const gl of glyphs) drawBolt(gl.x, gl.y, gl.size, gl.a, look.fill);
                // لحظة الضربة: الرمز أكبر قليلاً (×1.35) ثم يعود لحجمه — هذا هو «الوميض».
                for (const bu of bursts) {
                    const k = (nowMs - bu.start) / BURST_MS;
                    const p = map.project([bu.lon, bu.lat]);
                    drawBolt(p.x, p.y, basePx * (1.35 - 0.35 * k), 1 - 0.4 * k, look.fill);
                }
                ctx.globalAlpha = 1;

                // الوميض يحتاج إطارات متتالية؛ بلا ومضات نشطة يكفي النبض والحركة.
                if (bursts.length > 0 && !raf) raf = requestAnimationFrame(draw);
            };

            draw();
            map.on('render', draw);
            const beat = window.setInterval(draw, TICK_MS);
            return () => {
                map.off('render', draw);
                ro.disconnect();
                window.clearInterval(beat);
                if (raf) cancelAnimationFrame(raf);
                canvas.remove();
            };
        }
    }, [mapRef, enabled]);

    return null;
}
